// utils/voice-rotation.ts — FP7 §2 rotation continuity (PURE, no React, no I/O).
//
// The Qwen realtime socket is closed by the backend at the 2-minute policy wall
// (MAX_SOCKET_MS). Each ticket's JWT lives 60s (QWEN_REALTIME_TICKET_TTL_SECONDS),
// so the next-segment ticket must be minted IN-WINDOW — the contract pins
// T-45s of the 2-min wall, i.e. 45s before the wall, while the current ticket is
// still valid enough to overlap. This module holds the timing math + the
// rotation continuity assembly so both are directly unit-testable.

/** Backend policy wall per call segment (mirrors MAX_SOCKET_MS in qwen-realtime.ts). */
export const VOICE_POLICY_WALL_MS = 2 * 60 * 1000;
/** Mint the next ticket at T-45s of the wall (contract §2). */
export const VOICE_PREMINT_LEAD_MS = 45 * 1000;

/**
 * Delay (ms) from segment start until the in-window pre-mint should fire.
 * = wall − lead, floored at 0 (never negative). With the defaults this is 75s
 * (120s wall − 45s lead). PURE.
 */
export function premintDelayMs(
  wallMs: number = VOICE_POLICY_WALL_MS,
  leadMs: number = VOICE_PREMINT_LEAD_MS,
): number {
  return Math.max(0, wallMs - leadMs);
}

/**
 * Should we pre-mint now, given how long the current segment has been open?
 * True once `elapsedMs` reaches the pre-mint delay (T-45s) AND we are still
 * before the wall (no point pre-minting after the socket has already closed).
 * PURE.
 */
export function shouldPremintNow(
  elapsedMs: number,
  wallMs: number = VOICE_POLICY_WALL_MS,
  leadMs: number = VOICE_PREMINT_LEAD_MS,
): boolean {
  return elapsedMs >= premintDelayMs(wallMs, leadMs) && elapsedMs < wallMs;
}

export interface RotationTurn {
  role: 'user' | 'prof';
  text: string;
}

export interface RotationExchange {
  /** The most recent student utterance (cleaned), if any. */
  lastUser?: string;
  /** The most recent Prof line (cleaned), if any. */
  lastProf?: string;
}

const IMAGE_MARKER_RE = /\[\[image:[^\]]*\]\]/gi;

function clean(text: string, max: number): string {
  return text.replace(IMAGE_MARKER_RE, '').replace(/\s+/g, ' ').trim().slice(0, max);
}

/**
 * Extract the last user utterance + last Prof line from the rolling history, so
 * a resumed (rotated) call can replay the immediate thread on top of the rolling
 * `conversationSummary`. Each is capped to `maxChars` (well within the server's
 * 1,200-char item cap). PURE. Returns an empty object when there is nothing.
 */
export function lastExchangeForRotation(
  turns: RotationTurn[],
  maxChars = 600,
): RotationExchange {
  const out: RotationExchange = {};
  for (let i = turns.length - 1; i >= 0; i -= 1) {
    const t = turns[i];
    if (!out.lastUser && t.role === 'user') {
      const c = clean(t.text, maxChars);
      if (c) out.lastUser = c;
    }
    if (!out.lastProf && t.role === 'prof') {
      const c = clean(t.text, maxChars);
      if (c) out.lastProf = c;
    }
    if (out.lastUser && out.lastProf) break;
  }
  return out;
}

/**
 * Render the last exchange into a short replay block appended to the rolling
 * `conversationSummary` for the resumed segment, so Il Prof picks the thread up
 * naturally. Stays within the existing 1,200-char item cap when combined with a
 * ≤1,500-char summary header. PURE. Returns '' when there is nothing to replay.
 */
export function rotationReplayBlock(exchange: RotationExchange): string {
  const lines: string[] = [];
  if (exchange.lastUser) lines.push(`Ultima cosa detta dallo studente: ${exchange.lastUser}`);
  if (exchange.lastProf) lines.push(`Ultima tua risposta: ${exchange.lastProf}`);
  return lines.join('\n');
}

/**
 * Compose the resumed-segment summary: the rolling summary first, then the
 * immediate last-exchange replay block, capped to `maxChars` (the server's
 * 1,500-char conversationSummary field). PURE. The most recent content wins on
 * overflow (the rolling summary head is trimmed, never the replay tail).
 */
export function composeRotationSummary(
  rollingSummary: string | undefined,
  exchange: RotationExchange,
  maxChars = 1500,
): string | undefined {
  const replay = rotationReplayBlock(exchange);
  const parts = [rollingSummary?.trim(), replay].filter((p): p is string => Boolean(p && p.length > 0));
  if (parts.length === 0) return undefined;
  let composed = parts.join('\n');
  if (composed.length > maxChars) {
    // Keep the tail (the immediate exchange) — trim from the front, drop the
    // partial leading line.
    composed = composed.slice(composed.length - maxChars);
    const firstBreak = composed.indexOf('\n');
    if (firstBreak >= 0) composed = composed.slice(firstBreak + 1);
  }
  return composed || undefined;
}

/** FP7 §4 — founder-delegated VERBATIM reconnect copy, spoken at most once per
 *  rotation when a resumed segment opens. */
export const VOICE_ROTATION_RECONNECT_COPY_IT = '— riprendo subito da dove eravamo.';
